/**
 * A-03 注文履歴（FR-104・LC-08／**親注文グループ表示＝D-83/FR-128**）。
 *
 * ★ 状態も段の名前も**サーバが返した値をそのまま描く**（FR-107）＝
 *   画面が自前で「次はこれのはず」を計算しない＝卸の画面と違う状態が出ない。
 *
 * ★**M1.6＝接続卸を横断（fan-in）し、一括注文は親カードにまとめる**。
 *   - 結合キーは子注文の `parent_checkout_session_id`（§4.6）＝**クライアント側の結合**。
 *     親（`GET /partner/checkout-sessions`）は子の実体を返さない＝テナント分離を壊さないため。
 *   - **親自身は状態を持たない**（状態は子から導出）＝進捗バーは子カードにだけ出す。
 *   - **子が1件だけの親は、親の枠を出さない**＝「まとめ」が1件のときに枠を出すと、同じ注文が
 *     二重に見える。`CS-` は複数の卸にまたがったときだけ意味を持つ番号（D-85）。
 *   - **取れなかった子を黙って欠けさせない**＝接続が解除された卸の子は実体を取得できないので、
 *     親の `child_refs` との差分を「表示できないご注文があります」と出す（出さないと
 *     親の合計と子の合計が説明なく食い違う）。
 *
 * ★**M1.9b（Design 忠実化）＝この画面は「探す」ための一覧に徹する**。
 *   - 見かたを **すべて／現場ごと／卸ごと** の3つに分ける（Design `viewHistory` の resolveView）。
 *     現場ごと・卸ごとは**発注額の集計**で、そのまま原価の目安として読める。
 *   - 1件のカードは**コンパクト5行**（卸チップ・状態・お届け予定・品目要約・受付番号・進捗バー）。
 *     明細・くわしい経過・再注文は**タップした先の詳細（`OrderDetail`）へ移した**＝
 *     一覧に全部を積むと1画面に1件しか入らず、「どれだったか」を探す用が足せない。
 *   - 見かたは **URL の `?view=`**（`useState` ではなく）＝押した画面をそのまま人に渡せるのと、
 *     詳細から戻ったときに見かたが元に戻らない。履歴は `replace`＝戻るで画面を出るのが1回で済む。
 */

import type { CheckoutSession, ConnectedTenant, PartnerOrder, partnerApi } from "@kenzai/api-client";
import { Badge, Empty, ErrorNote, formatDate, formatDateTime, Money, ProgressBar, Skeleton } from "@kenzai/ui";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { ParentSheet } from "./ParentSheet";
import { PageTitle } from "../Shell";
import { useIsDesktop } from "../useIsDesktop";

type Client = ReturnType<typeof partnerApi>;

/** 見かた＝すべて／現場ごと（送付先で集計）／卸ごと（取引先で集計）。 */
type View = "all" | "site" | "vendor";

const SEGMENTS: { view: View; label: string; testid: string }[] = [
  { view: "all", label: "すべて", testid: "seg-all" },
  { view: "site", label: "現場ごと", testid: "seg-by-site" },
  { view: "vendor", label: "卸ごと", testid: "seg-by-vendor" },
];

/** 現場が入っていない注文の置き場所。**黙って落とさない**（合計が合わなくなる）。 */
const NO_SITE = "（現場の指定なし）";

/* ------------------------------------------------------------------ */
/* アイコン（Design の ICO と同じ図形・ライブラリは足さない）             */
/* ------------------------------------------------------------------ */

function Svg({ size, width, children }: { size: number; width?: number; children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={width ?? 1.9}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      style={{ flex: "none" }}
    >
      {children}
    </svg>
  );
}

/** 配送中・本日お届けの印（詳細でも同じ絵を使う＝同じ意味に同じ形）。 */
export function TruckIcon({ size = 13 }: { size?: number }) {
  return (
    <Svg size={size}>
      <rect x="1" y="5" width="14" height="10" rx="1" />
      <path d="M15 8h4l3 3v4h-7z" />
      <circle cx="6" cy="17" r="1.8" />
      <circle cx="18" cy="17" r="1.8" />
    </Svg>
  );
}

/** 済んだ段の印（詳細の os-mark の中）。 */
export function CheckIcon({ size = 12 }: { size?: number }) {
  return (
    <Svg size={size} width={2.4}>
      <polyline points="4 12 9 17 20 6" />
    </Svg>
  );
}

function PinIcon() {
  return (
    <Svg size={14}>
      <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z" />
      <circle cx="12" cy="10" r="3" />
    </Svg>
  );
}

function StoreIcon() {
  return (
    <Svg size={14}>
      <path d="M4 4h16l1 5a3 3 0 0 1-3 3 3 3 0 0 1-3-3 3 3 0 0 1-6 0 3 3 0 0 1-3 3 3 3 0 0 1-3-3z" />
      <path d="M5 12v8h14v-8" />
      <path d="M9 20v-5h6v5" />
    </Svg>
  );
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <span
      // 開いているときは下向き＝閉じた行と開いた行が並んでも、どちらが開いているか形で分かる
      style={{ display: "inline-flex", transform: open ? "rotate(90deg)" : undefined }}
    >
      <Svg size={15} width={2.2}>
        <polyline points="9 6 15 12 9 18" />
      </Svg>
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* 集計（現場ごと・卸ごと）                                              */
/* ------------------------------------------------------------------ */

/** 取消は**カードには出すが金額には数えない**（A-08 の集計と同じ扱い＝画面ごとに違う額を出さない）。 */
function isCancelled(o: PartnerOrder): boolean {
  return o.progress_index < 0;
}

/**
 * 発注額＝**注文に保存済みの商品計**（税抜）。配送料は足さない＝A-08「現場・原価管理」と
 * 同じ式にする（材料費の集計に運賃を混ぜると、2つの画面で違う額が出る）。
 * 単価が未確定（`null`）の注文は 0 として扱う＝フッターに「確認中の数量を除く」と書く。
 */
function amountOf(o: PartnerOrder): number {
  return isCancelled(o) ? 0 : Number(o.total_amount ?? 0);
}

type Group = { key: string; rows: PartnerOrder[]; total: number };

function groupBy(orders: PartnerOrder[], keyOf: (o: PartnerOrder) => string): Group[] {
  const groups: Group[] = [];
  for (const o of orders) {
    const key = keyOf(o);
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, rows: [], total: 0 };
      groups.push(g);
    }
    g.rows.push(o);
    g.total += amountOf(o);
  }
  // 金額の大きい順＝「どこにいくら出しているか」から読む画面なので、件数順や名前順にしない
  return groups.sort((a, b) => b.total - a.total);
}

/* ------------------------------------------------------------------ */
/* 画面                                                                */
/* ------------------------------------------------------------------ */

/**
 * 見出し＋セグメント（M1.9b）＝どの状態（ロード中・空・通常）でも同じ頭を出す。
 *
 * ★**スマホと PC で帯の意味が違う**（`useIsDesktop` で排他レンダリング）：
 *   - スマホ＝**すべて／現場ごと／卸ごと**（1件ずつのカードを積む画面なので、集計で束ねる
 *     見かたが要る）。
 *   - PC＝Design どおり**すべての注文／現場・原価管理**（表で一覧できるので束ねる必要が無く、
 *     広い画面で本当に見たいのは現場ごとの原価＝別画面）。**`seg-orders`／`seg-sites` は
 *     `/sites` への唯一の導線**（E2E site-costs.spec がこの経路で入る）。
 *   同じ testid を2つ DOM に出さないため、**どちらか片方だけを描く**。
 */
export function OrdersSegments({ on }: { on: "orders" | "sites" }) {
  return (
    <nav className="kz-seg" aria-label="注文履歴と現場・原価管理を切り替え">
      <Link to="/orders" className={on === "orders" ? "kz-seg--on" : ""} data-testid="seg-orders">
        すべての注文
      </Link>
      <Link to="/sites" className={on === "sites" ? "kz-seg--on" : ""} data-testid="seg-sites">
        現場・原価管理
      </Link>
    </nav>
  );
}

function OrdersHeader({ view, isDesktop }: { view: View; isDesktop: boolean }) {
  if (isDesktop) {
    return (
      <div>
        <div className="kp-head">
          <h1>注文履歴</h1>
          <p>接続中のすべてのお取引先のご注文を、受付の新しい順に並べています。</p>
        </div>
        <div style={{ marginBottom: "var(--kz-space-4)" }}>
          <OrdersSegments on="orders" />
        </div>
      </div>
    );
  }
  return (
    <div>
      <PageTitle>注文履歴</PageTitle>
      <nav className="kz-seg" aria-label="注文の見かたを切り替え" style={{ marginBottom: "var(--kz-space-4)" }}>
        {SEGMENTS.map((s) => (
          <Link
            key={s.view}
            // ★`replace`＝見かたを切り替えるたびに履歴が積まれると、戻るを3回押さないと
            //   この画面から出られない（見かたの切替は「移動」ではない）。
            replace
            to={s.view === "all" ? "/orders" : `/orders?view=${s.view}`}
            className={view === s.view ? "kz-seg--on" : ""}
            data-testid={s.testid}
          >
            {s.label}
          </Link>
        ))}
      </nav>
    </div>
  );
}

export function Orders({ client, connections }: { client: Client; connections: ConnectedTenant[] }) {
  // 停止中の卸は注文API も 403（03 §8）＝引きに行かず、履歴が欠けている旨をその場に出す。
  const usable = connections.filter((c) => c.tenant_status !== "suspended");
  const suspended = connections.filter((c) => c.tenant_status === "suspended");
  const [sheetId, setSheetId] = useState<number | null>(null);
  // 集計グループの開閉（既定は開＝Design）。閉じたものだけを覚える。
  const [closed, setClosed] = useState<Record<string, boolean>>({});
  // 空状態の CTA 用（下の早期 return より前に呼ぶ＝hook の順序を固定する）
  const nav = useNavigate();
  const [params] = useSearchParams();
  const raw = params.get("view");
  const view: View = raw === "site" || raw === "vendor" ? raw : "all";
  // ★PC は Design の表（`OrdersTable`）＝スマホのカードとは**排他**（同じ testid を二重に出さない）
  const isDesktop = useIsDesktop();
  /** 表の中で明細を開いている注文（PC のみ）。 */
  const [openRow, setOpenRow] = useState<number | null>(null);

  const orderQueries = useQueries({
    queries: usable.map((c) => ({
      queryKey: ["partner-orders", c.tenant_id],
      queryFn: () => client.orders(c.tenant_id),
      // 承認されたら進捗が動く＝開いている間は追いかける（05 §5.2 の受注箱と同じ考え）
      refetchInterval: 30_000,
    })),
  });
  // 親注文（`CS-`）。**1ページ目だけ**引く＝古い親が拾えないときは子が単独カードで並ぶ
  // （情報は欠けない）。ページングを足すのは「もっと見る」を作るとき（M2）。
  const parents = useQuery({ queryKey: ["checkout-sessions"], queryFn: () => client.checkoutSessions() });

  // ★待たせ方だけを変える（M1.7）＝**全卸ぶんが揃うまで待つ構造は不変**。
  //   ロード中に `data-testid="orders"` や注文由来の testid は出さない。
  if (orderQueries.some((q) => q.isLoading)) {
    return (
      <>
        <OrdersHeader view={view} isDesktop={isDesktop} />
        <OrdersSkeleton />
      </>
    );
  }
  const failed = orderQueries.find((q) => q.isError);

  // **卸をまたいで1本の時系列**（M1.8 で絞り込みが無くなった＝これが唯一の見え方）
  const shown: PartnerOrder[] = usable
    .flatMap((_c, i) => orderQueries[i]?.data?.items ?? [])
    // 受付が新しい順
    .sort((a, b) => b.received_at.localeCompare(a.received_at));
  const fetchedIds = new Set(shown.map((o) => o.order_id));

  // 1社でも失敗したら黙らせない（＝接続が解除された卸はここで理由が出る・FR-117）
  if (failed && shown.length === 0) {
    return (
      <>
        <OrdersHeader view={view} isDesktop={isDesktop} />
        <ErrorNote error={failed.error} />
      </>
    );
  }
  if (shown.length === 0 && !failed) {
    return (
      <>
        <OrdersHeader view={view} isDesktop={isDesktop} />
        <Empty
          title="まだご注文はありません"
          hint="「注文する」から商品を選んでください。"
          // ★カートの空状態と同じ理由で `<button>`（下部タブの同名リンクと衝突させない）。
          action={
            <button
              type="button"
              data-testid="empty-go-catalog-orders"
              className="kz-btn kz-btn--primary"
              onClick={() => nav("/catalog")}
              style={{ minHeight: 48, minWidth: 200 }}
            >
              注文する画面へ
            </button>
          }
        />
      </>
    );
  }

  const showVendorName = connections.length > 1;
  const parentOf = (id: number | null): CheckoutSession | undefined =>
    id === null ? undefined : parents.data?.items.find((p) => p.checkout_session_id === id);
  const sheetParent = sheetId === null ? undefined : parentOf(sheetId);

  return (
    <div style={{ display: "grid", gap: "var(--kz-space-3)" }} data-testid="orders">
      <OrdersHeader view={view} isDesktop={isDesktop} />
      {failed && <ErrorNote error={failed.error} />}
      {suspended.map((c) => (
        <p
          key={c.tenant_id}
          style={{ margin: 0, fontSize: "var(--kz-font-size-sm)", color: "var(--kz-status-danger)" }}
        >
          {c.tenant_name} は現在ご利用いただけないため、この卸のご注文は表示できません。
        </p>
      ))}

      {isDesktop ? (
        // ★PC＝Design の表（`kp-otbl`）。スマホの3ビュー（すべて／現場ごと／卸ごと）は
        //   出さない＝表なら一覧で見比べられるので束ねる必要が無く、Design PC にも無い。
        //   現場ごとの集計は隣の「現場・原価管理」が持つ（セグメントで行き来する）。
        <OrdersTable
          shown={shown}
          fetchedIds={fetchedIds}
          parentOf={parentOf}
          openRow={openRow}
          onToggleRow={(id) => setOpenRow(openRow === id ? null : id)}
          onPrint={setSheetId}
        />
      ) : view === "all" ? (
        <AllView
          shown={shown}
          fetchedIds={fetchedIds}
          parentOf={parentOf}
          showVendorName={showVendorName}
          onPrint={setSheetId}
        />
      ) : (
        <GroupedView
          shown={shown}
          view={view}
          showVendorName={showVendorName}
          closed={closed}
          onToggle={(k) => setClosed((s) => ({ ...s, [k]: !s[k] }))}
        />
      )}

      {sheetParent && (
        <ParentSheet
          parent={sheetParent}
          orders={shown.filter((o) => o.parent_checkout_session_id === sheetParent.checkout_session_id)}
          onClose={() => setSheetId(null)}
        />
      )}
    </div>
  );
}

/**
 * 「すべて」＝時系列の一覧。**まとめ注文（親 `CS-`）はまとめのまま**出す。
 * 集計の2ビューと違ってここは「いつ何を頼んだか」を辿る場所なので、注文の単位を崩さない。
 */
function AllView({
  shown,
  fetchedIds,
  parentOf,
  showVendorName,
  onPrint,
}: {
  shown: PartnerOrder[];
  fetchedIds: Set<number>;
  parentOf: (id: number | null) => CheckoutSession | undefined;
  showVendorName: boolean;
  onPrint: (id: number) => void;
}) {
  // ① **「今日届く」を最上部**（FR-104）＝現場が一番知りたいのはこれ。**卸横断のまま**。
  const today = shown.filter((o) => o.is_arriving_today);
  const rest = shown.filter((o) => !o.is_arriving_today);

  // まとめカードにする親（子が2件以上）を、時系列の順番を保ったまま1回だけ出す。
  // ★**「今日届く」に上げた子も親の位置決めには数える**＝全部が今日届く注文だったときに
  //   親カードごと消えると、まとめ番号（CS-）と一括確認シートに辿り着けなくなる。
  //   カード自体は上のセクションと二重に出さない（親の中には「今日届く」以外だけを並べる）。
  const todayIds = new Set(today.map((o) => o.order_id));
  const emitted = new Set<number>();
  const blocks: { key: string; node: React.ReactNode }[] = [];
  for (const o of shown) {
    const parent = parentOf(o.parent_checkout_session_id);
    if (parent && parent.child_refs.length >= 2) {
      if (emitted.has(parent.checkout_session_id)) continue;
      emitted.add(parent.checkout_session_id);
      blocks.push({
        key: `c${parent.checkout_session_id}`,
        node: (
          <ParentCard
            parent={parent}
            orders={rest.filter((x) => x.parent_checkout_session_id === parent.checkout_session_id)}
            movedToToday={today.filter((x) => x.parent_checkout_session_id === parent.checkout_session_id).length}
            missing={parent.child_refs.filter((r) => !fetchedIds.has(r.order_id)).length}
            showVendorName={showVendorName}
            onPrint={() => onPrint(parent.checkout_session_id)}
          />
        ),
      });
      continue;
    }
    if (todayIds.has(o.order_id)) continue; // 単独カードは「今日届く」で出している
    blocks.push({
      key: `o${o.order_id}`,
      node: <OrderCard order={o} showVendorName={showVendorName} />,
    });
  }

  return (
    <div>
      {today.length > 0 && (
        <section>
          {/* ★`arriving-today` は**セクション行そのもの**に付ける（節の器ではない）＝
              「今日届く N件」という見出しが出たことを指す印にする。 */}
          <div className="ord-sec today" data-testid="arriving-today">
            <TruckIcon />
            <b>今日届く</b>
            <small className="kz-num">{today.length}件</small>
          </div>
          {today.map((o) => (
            <OrderCard key={o.order_id} order={o} showVendorName={showVendorName} />
          ))}
        </section>
      )}
      {blocks.length > 0 && (
        <section>
          <div className="ord-sec">
            <b>{today.length > 0 ? "その他の注文" : "これまでの注文"}</b>
            <small className="kz-num">{blocks.length}件</small>
          </div>
          {blocks.map((b) => (
            <div key={b.key}>{b.node}</div>
          ))}
        </section>
      )}
      <div className="oa-foot">受付・確定・出荷のタイミングで LINE にもお知らせが届きます。</div>
    </div>
  );
}

/**
 * 「現場ごと」「卸ごと」＝**発注額の集計**。まとめ注文は解いて、卸ごとの注文（`JU-`）を平らに並べる
 * （親 `CS-` は複数の現場・複数の卸にまたがるので、どちらのグループにも入れられない）。
 */
function GroupedView({
  shown,
  view,
  showVendorName,
  closed,
  onToggle,
}: {
  shown: PartnerOrder[];
  view: Exclude<View, "all">;
  showVendorName: boolean;
  closed: Record<string, boolean>;
  onToggle: (key: string) => void;
}) {
  const bySite = view === "site";
  const groups = groupBy(shown, (o) => (bySite ? (o.site_name ?? NO_SITE) : o.tenant_name));
  const grand = groups.reduce((s, g) => s + g.total, 0);

  return (
    <div>
      <div className="hg-grand" data-testid="costs-grand">
        <span>
          {bySite ? "全現場の発注額合計" : "全卸への発注額合計"}
          <small>税抜</small>
        </span>
        <b>
          <Money value={String(grand)} />
        </b>
      </div>

      {groups.map((g) => {
        const stateKey = `${view}:${g.key}`;
        const open = !closed[stateKey];
        return (
          <div className="hg" key={g.key} data-testid={`group-${g.key}`}>
            <button
              type="button"
              className="hg-head kz-pressable"
              aria-expanded={open}
              onClick={() => onToggle(stateKey)}
              // ★`button` の既定（UA のフォント・文字色）を消すだけ。Design は素の CSS に
              //   `button { font: inherit; color: inherit }` を持っているが、こちらの
              //   `liff.css` にはその全体リセットが無いので、クラスを当てただけでは字が変わる。
              style={{ font: "inherit", color: "inherit", cursor: "pointer" }}
            >
              <span className="hg-n">
                {bySite ? <PinIcon /> : <StoreIcon />}
                <b>{g.key}</b>
                <small className="kz-num">{g.rows.length}件</small>
              </span>
              <span className="hg-t">
                <small>発注額</small>
                <b>
                  <Money value={String(g.total)} />
                </b>
                <ChevronIcon open={open} />
              </span>
            </button>
            {open && (
              <div className="hg-body">
                {g.rows.map((o) => (
                  <OrderCard key={o.order_id} order={o} showVendorName={showVendorName} />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* ★Design の foot を踏襲。ただし**取消も除く**ことは書き足す（Design に取消の概念が無い）。
          「現場ごとの集計は原価管理に使えます」は現場ビューでだけ言う（卸ビューでは意味が合わない）。 */}
      <div className="oa-foot">
        発注額は、確認中のご注文と取消分を除いた税抜の合計です。
        {bySite && "現場ごとの集計は、そのまま原価管理にお使いいただけます。"}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* PC の表（Design `kp-otbl`）＝1件1行で見比べる                          */
/* ------------------------------------------------------------------ */

/** 表の列数（セクション行・明細行の `colSpan` はここを見る＝列を足したら1箇所直せばよい）。 */
const TABLE_COLS = 8;

/** 「石膏ボード 12.5mm × 60枚、軽天スタッド 65形 × 20本 ほか2品目」＝表の品目セル。 */
function itemsSummaryTable(order: PartnerOrder): string {
  const head = order.items
    .slice(0, 2)
    .map((i) => `${i.name} × ${Number(i.qty)}${i.unit ?? ""}`)
    .join("、");
  const rest = order.items.length - 2;
  return rest > 0 ? `${head} ほか${rest}品目` : head;
}

/**
 * 進捗の丸（Design のミニ stagebar）。
 * ★**サーバが返した段（`progress_steps`）の数だけ描く**＝画面で「次はこれのはず」を計算しない
 *   （FR-107）。段が増減しても付いていく。
 */
function StageDots({ steps, index }: { steps: string[]; index: number }) {
  return (
    <span className="kp-dots" aria-hidden="true">
      {steps.map((step, i) => {
        const done = index >= 0 && i <= index;
        return (
          <span key={step} style={{ display: "contents" }}>
            {i > 0 && <span className={`kp-dotline${done ? " done" : ""}`} />}
            <span className={`kp-dot${done ? " done" : ""}${i === index ? " cur" : ""}`} />
          </span>
        );
      })}
    </span>
  );
}

/** 表全体。まとめ注文（親）は **`<tbody>` 1つ**にまとめる＝行の入れ子は作れないため。 */
function OrdersTable({
  shown,
  fetchedIds,
  parentOf,
  openRow,
  onToggleRow,
  onPrint,
}: {
  shown: PartnerOrder[];
  fetchedIds: Set<number>;
  parentOf: (id: number | null) => CheckoutSession | undefined;
  openRow: number | null;
  onToggleRow: (id: number) => void;
  onPrint: (id: number) => void;
}) {
  // 「今日届く」は最上部（FR-104）＝現場が一番知りたいのはこれ。**卸横断のまま**。
  const today = shown.filter((o) => o.is_arriving_today);
  const todayIds = new Set(today.map((o) => o.order_id));

  // まとめ（親）は時系列の位置に1回だけ。**「今日届く」に上げた子も位置決めには数える**＝
  // 全部が今日届く注文だったときに親ごと消えると、まとめ番号と一括確認シートに辿り着けない。
  const emitted = new Set<number>();
  const blocks: React.ReactNode[] = [];
  for (const o of shown) {
    const parent = parentOf(o.parent_checkout_session_id);
    if (parent && parent.child_refs.length >= 2) {
      if (emitted.has(parent.checkout_session_id)) continue;
      emitted.add(parent.checkout_session_id);
      const children = shown.filter(
        (x) => x.parent_checkout_session_id === parent.checkout_session_id && !todayIds.has(x.order_id),
      );
      const movedToToday = parent.child_refs.length - children.length;
      blocks.push(
        // ★親は **`<tbody>`**＝`<tr>` の中に `<tr>` は置けないので、まとめヘッダー行と
        //   子の行を1つの `tbody` で束ねる（`checkout-{id}` の中に `order-{id}` が入る、
        //   という E2E の見方をそのまま満たせる形）。
        <tbody key={`c${parent.checkout_session_id}`} data-testid={`checkout-${parent.checkout_session_id}`}>
          <tr>
            <td colSpan={TABLE_COLS} style={{ background: "var(--kz-color-surface-sunken)" }}>
              <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                <StoreIcon />
                <b>
                  まとめて注文（<span className="kz-num">{parent.child_refs.length}</span>社）
                </b>
                <span className="kz-num" style={{ fontSize: 12, color: "var(--kz-color-ink-muted)" }}>
                  {parent.display_no}
                </span>
                <span className="kz-num" style={{ fontSize: 12, color: "var(--kz-color-ink-faint)" }}>
                  {formatDateTime(parent.created_at)} 受付
                </span>
                <b className="kz-num" style={{ marginLeft: "auto" }}>
                  <Money value={parent.total_amount} />
                </b>
                <button
                  type="button"
                  onClick={() => onPrint(parent.checkout_session_id)}
                  data-testid={`parent-sheet-${parent.checkout_session_id}`}
                  className="kp-act"
                >
                  一括確認シートを見る・印刷する
                </button>
              </span>
              {movedToToday > 0 && (
                <span style={{ display: "block", marginTop: 4, fontSize: 11, color: "var(--kz-color-ink-muted)" }}>
                  {children.length === 0
                    ? "このご注文は、すべて上の「今日届く」に表示しています。"
                    : `うち ${movedToToday} 件は「今日届く」に表示しています。`}
                </span>
              )}
              {parent.child_refs.filter((r) => !fetchedIds.has(r.order_id)).length > 0 && (
                // 黙って欠けさせない（親の合計と子の合計が説明なく食い違って見える）
                <span
                  data-testid={`missing-children-${parent.checkout_session_id}`}
                  style={{ display: "block", marginTop: 4, fontSize: 11, color: "var(--kz-status-warn)" }}
                >
                  表示できないご注文があります（お取引が終了した取引先のご注文）。
                </span>
              )}
            </td>
          </tr>
          {children.map((c) => (
            <OrderRow
              key={c.order_id}
              order={c}
              open={openRow === c.order_id}
              onToggle={() => onToggleRow(c.order_id)}
            />
          ))}
        </tbody>,
      );
      continue;
    }
    if (todayIds.has(o.order_id)) continue; // 単独の行は「今日届く」の節で出している
    blocks.push(
      <tbody key={`o${o.order_id}`}>
        <OrderRow order={o} open={openRow === o.order_id} onToggle={() => onToggleRow(o.order_id)} />
      </tbody>,
    );
  }

  // 右肩の件数（Design の toolbar note）＝取消はどちらにも数えない
  const live = shown.filter((o) => !isCancelled(o) && o.status !== "納品済み").length;
  const done = shown.filter((o) => o.status === "納品済み").length;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8 }}>
        <span style={{ fontSize: 12.5, color: "var(--kz-color-ink-faint)" }} className="kz-num">
          受付済み〜配送中 {live}件・納品済み {done}件
        </span>
      </div>

      <div className="kp-tblwrap">
        <table className="kp-tbl kp-otbl">
          <thead>
            <tr>
              <th>注文番号</th>
              <th>お取引先</th>
              <th>お届け先</th>
              <th>品目</th>
              <th>お届け</th>
              <th className="num">金額（税抜）</th>
              <th>進捗</th>
              <th aria-label="明細の開閉" />
            </tr>
          </thead>

          {today.length > 0 && (
            <tbody data-testid="arriving-today">
              <tr className="kp-secrow">
                <td colSpan={TABLE_COLS}>
                  <span className="kp-sec">
                    <TruckIcon size={16} />
                    <b>今日届く</b>
                    <small className="kz-num">{today.length}件</small>
                  </span>
                </td>
              </tr>
              {today.map((o) => (
                <OrderRow
                  key={o.order_id}
                  order={o}
                  open={openRow === o.order_id}
                  onToggle={() => onToggleRow(o.order_id)}
                />
              ))}
            </tbody>
          )}

          {blocks.length > 0 && (
            <tbody>
              <tr className="kp-secrow">
                <td colSpan={TABLE_COLS}>
                  <span className="kp-sec">
                    <b>{today.length > 0 ? "その他の注文" : "ご注文の履歴"}</b>
                    <small className="kz-num">{blocks.length}件</small>
                  </span>
                </td>
              </tr>
            </tbody>
          )}
          {blocks}
        </table>
      </div>

      <div className="oa-foot">
        受付・確定・出荷のタイミングで LINE にもお知らせが届きます。行をクリックすると明細を表示します。
      </div>
    </div>
  );
}

/** 表の1行＋（開いていれば）明細の行。 */
function OrderRow({ order, open, onToggle }: { order: PartnerOrder; open: boolean; onToggle: () => void }) {
  const cancelled = isCancelled(order);
  const today = order.is_arriving_today && !cancelled;
  const fee = order.delivery_fee === null ? null : Number(order.delivery_fee);
  const itemsTotal = order.total_amount === null ? null : Number(order.total_amount);
  const grand = itemsTotal === null ? null : itemsTotal + (fee ?? 0);

  return (
    <>
      <tr
        data-testid={`order-${order.order_id}`}
        className={`clickable${open ? " kp-open" : ""}`}
        onClick={onToggle}
      >
        <td>
          {/* ★受付番号は**詳細へのリンク**（通知のディープリンクと同じ着地点＝D-88）。
              行のクリック（明細の開閉）と混ざらないよう、ここでイベントを止める。 */}
          <Link
            to={`/orders/${order.tenant_id}/${order.order_id}`}
            className="kz-num"
            onClick={(e) => e.stopPropagation()}
            style={{ fontWeight: 600 }}
          >
            {order.display_no}
          </Link>
          <span className="sub kz-num">{formatDateTime(order.received_at)} 受付</span>
        </td>
        <td>{order.tenant_name}</td>
        <td className="kp-site">{order.site_name ?? "確認中"}</td>
        <td>
          <span className="kp-summary">{itemsSummaryTable(order)}</span>
        </td>
        <td data-testid={`eta-${order.order_id}`}>
          {today ? (
            <span className="ord-today">
              <TruckIcon />
              本日お届け
            </span>
          ) : (
            <>
              {formatDate(order.desired_date)}
              {order.bin_name && <span className="sub">{order.bin_name}</span>}
            </>
          )}
        </td>
        <td className="num">
          <Money value={grand === null ? null : String(grand)} />
        </td>
        <td data-testid={`progress-${order.order_id}`}>
          {cancelled ? (
            <Badge tone="danger">取消</Badge>
          ) : (
            <span className="kp-stage">
              {/* ★状態ピルが**いまの段**そのもの（サーバの語をそのまま出す＝FR-107）。
                  `progress-current` はここに置く＝E2E が「いまどの段か」を読む唯一の指し先で、
                  スマホのバー（ProgressBar variant="bar"）と同じ意味・同じ名前。 */}
              <span
                data-testid="progress-current"
                className={`po-st ${STATUS_CLASS[order.status] ?? "ps-recv"}`}
              >
                {order.status}
              </span>
              <StageDots steps={order.progress_steps} index={order.progress_index} />
            </span>
          )}
        </td>
        <td>
          <button
            type="button"
            className="kp-chev"
            aria-expanded={open}
            aria-label={`${order.display_no ?? "この注文"} の明細を${open ? "閉じる" : "開く"}`}
            onClick={(e) => {
              // 行にも同じ開閉が付いている＝止めないと2回走って開かない
              e.stopPropagation();
              onToggle();
            }}
          >
            <Svg size={15} width={2.2}>
              <polyline points="6 9 12 15 18 9" />
            </Svg>
          </button>
        </td>
      </tr>

      {open && (
        <tr className="kp-items">
          <td colSpan={TABLE_COLS}>
            <div className="kp-lines">
              {order.items.map((it, i) => (
                <div className="kp-line" key={i}>
                  <span className="kp-line-n">
                    {it.name}
                    {it.spec && <small>{it.spec}</small>}
                  </span>
                  <span className="kp-line-q kz-num">
                    {Number(it.qty)}
                    {it.unit ?? ""}
                    {it.unit_price !== null && ` × ¥${Number(it.unit_price).toLocaleString("ja-JP")}`}
                  </span>
                  <span className="kp-line-a">
                    <Money value={it.amount} />
                  </span>
                </div>
              ))}
              {/* 配送料は「別途」の卸だけ（D-90＝「込み」の卸には行を出さない） */}
              {fee !== null && (
                <div className="kp-line">
                  <span className="kp-line-n">配送料</span>
                  <span className="kp-line-q" />
                  <span className="kp-line-a">
                    <Money value={String(fee)} />
                  </span>
                </div>
              )}
              <div className="kp-line total">
                <span className="kp-line-n">合計（税抜）</span>
                <span className="kp-line-a">
                  <Money value={grand === null ? null : String(grand)} />
                </span>
              </div>
            </div>

            {order.timeline.length > 0 && (
              <div style={{ display: "flex", gap: 14, flexWrap: "wrap", padding: "10px 4px 2px" }}>
                {order.timeline.map((t, i) => (
                  <span key={i} style={{ fontSize: 12, color: "var(--kz-color-ink-muted)" }}>
                    <b style={{ color: "var(--kz-color-ink)" }}>{t.step}</b>{" "}
                    <span className="kz-num">{formatDateTime(t.at)}</span>
                  </span>
                ))}
              </div>
            )}

            {/* ★「同じ内容で再注文」は**注文詳細**に置いてある（M1.9b の分業＝一覧は探す場所）。
                ここには入口だけを出す＝カートを書き換える操作を、一覧の行の中に増やさない。 */}
            <p style={{ margin: "8px 4px 2px", fontSize: 12.5 }}>
              <Link to={`/orders/${order.tenant_id}/${order.order_id}`}>
                この注文の詳細（くわしい経過・同じ内容で再注文）
              </Link>
            </p>
          </td>
        </tr>
      )}
    </>
  );
}

/**
 * 読み込み中の注文履歴（M1.7）＝注文カードの**形**（状態行・お届け予定・進捗バー）で待たせる。
 * 素の div の帯だけ＝testid も表も出さない。
 */
function OrdersSkeleton() {
  return (
    <div aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="ord-card">
          <div style={{ display: "flex", gap: "var(--kz-space-3)" }}>
            <div className="kz-skeleton" style={{ height: 14, width: 96 }} />
            <div className="kz-skeleton" style={{ height: 14, width: 60, marginLeft: "auto" }} />
          </div>
          <div className="kz-skeleton" style={{ height: 18, width: 150, marginTop: 6 }} />
          <div style={{ marginTop: 4 }}>
            <Skeleton rows={2} height={12} />
          </div>
          <div style={{ display: "flex", gap: 3, marginTop: "var(--kz-space-2)" }}>
            {[0, 1, 2, 3, 4].map((n) => (
              <div key={n} className="kz-skeleton" style={{ height: 5, flex: 1, borderRadius: 3 }} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/**
 * 一括注文の親カード（`CS-`）。**進捗は持たない**（子から導出＝親に状態を作らない）。
 * 見た目は集計グループ（`.hg`）と同じ「見出し＋左の罫線」＝まとめの中身であることを字で書かずに示す。
 */
function ParentCard({
  parent,
  orders,
  movedToToday,
  missing,
  showVendorName,
  onPrint,
}: {
  parent: CheckoutSession;
  orders: PartnerOrder[];
  movedToToday: number;
  missing: number;
  showVendorName: boolean;
  onPrint: () => void;
}) {
  return (
    <div className="hg" data-testid={`checkout-${parent.checkout_session_id}`}>
      {/* ★ここは**押せない見出し**（`.hg-head` と同じ版組の div）＝中に「一括確認シート」の
          ボタンを置くので、見出しごとボタンにすると入れ子のボタンになる。 */}
      <div className="hg-head" style={{ display: "grid", gap: "var(--kz-space-2)" }}>
        <span style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
          <span className="hg-n">
            <StoreIcon />
            <b>
              まとめて注文（<span className="kz-num">{parent.child_refs.length}</span>社）
            </b>
            <small className="kz-num">{parent.display_no}</small>
          </span>
          <span className="hg-t">
            <b>
              <Money value={parent.total_amount} />
            </b>
          </span>
        </span>
        <span style={{ fontSize: "10.5px", color: "var(--kz-color-ink-faint)" }}>
          {formatDateTime(parent.created_at)} 受付
        </span>
        {movedToToday > 0 && (
          <span style={{ fontSize: "11px", color: "var(--kz-color-ink-muted)" }}>
            {orders.length === 0
              ? "このご注文は、すべて上の「今日届く」に表示しています。"
              : `うち ${movedToToday} 件は「今日届く」に表示しています。`}
          </span>
        )}
        {missing > 0 && (
          // 黙って欠けさせない（親の合計と子の合計が食い違って見える）
          <span
            data-testid={`missing-children-${parent.checkout_session_id}`}
            style={{ fontSize: "11px", color: "var(--kz-status-warn)" }}
          >
            表示できないご注文があります（お取引が終了した取引先のご注文）。
          </span>
        )}
        <button
          type="button"
          onClick={onPrint}
          data-testid={`parent-sheet-${parent.checkout_session_id}`}
          className="kz-btn kz-btn--flat kz-pressable"
          style={{ justifySelf: "start", minHeight: 44 }}
        >
          一括確認シートを見る・印刷する
        </button>
      </div>
      {orders.length > 0 && (
        <div className="hg-body">
          {orders.map((o) => (
            <OrderCard key={o.order_id} order={o} showVendorName={showVendorName} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* 注文カード（コンパクト＝タップで詳細へ）                               */
/* ------------------------------------------------------------------ */

/** 職人語彙（LC-08）→ ピルの色。**画面は状態を計算しない**＝サーバの文字列の見た目対応表だけ。 */
const STATUS_CLASS: Record<string, string> = {
  受付済み: "ps-recv",
  確定: "ps-conf",
  出荷済み: "ps-ship",
  配送中: "ps-transit",
  納品済み: "ps-done",
};

/** 「石膏ボード 12.5mm 他2点」＝一覧では品名は1つだけ。全明細は詳細で見る。 */
function itemsSummary(order: PartnerOrder): string {
  const [first, ...others] = order.items;
  if (!first) return "";
  return others.length > 0 ? `${first.name} 他${others.length}点` : first.name;
}

// ★M1.9b で**この画面の中だけの部品**になった（以前は OrderDetail が同じカードを再利用していた）。
//   詳細は「1件を読む」ための別の版組（os-rail）を持つ＝一覧のカードと兼用しない。
function OrderCard({ order, showVendorName = false }: { order: PartnerOrder; showVendorName?: boolean }) {
  const cancelled = isCancelled(order);
  const today = order.is_arriving_today && !cancelled;

  return (
    <Link
      to={`/orders/${order.tenant_id}/${order.order_id}`}
      data-testid={`order-${order.order_id}`}
      className={`ord-card${today ? " transit" : ""}`}
      // ブラウザ既定のリンク色・下線を消すだけ（見た目はクラスが持つ）
      style={{ color: "inherit", textDecoration: "none" }}
    >
      <span className="ord-top">
        {showVendorName && <span className="ord-sp">{order.tenant_name}</span>}
        {/* 卸チップを出さない（1社だけの接続）ときも、状態は右端に置く */}
        <span style={{ marginLeft: "auto" }}>
          {cancelled ? (
            <Badge tone="danger">取消</Badge>
          ) : today ? (
            <span className="ord-today">
              <TruckIcon />
              本日お届け
            </span>
          ) : (
            // 未知の状態はグレー（中立）で出す＝勝手に色の意味を作らない
            <span className={`po-st ${STATUS_CLASS[order.status] ?? "ps-recv"}`}>{order.status}</span>
          )}
        </span>
      </span>

      <span className="ord-eta">
        <small>お届け予定</small>
        <b data-testid={`eta-${order.order_id}`}>
          {formatDate(order.desired_date)}
          {order.bin_name && `・${order.bin_name}`}
        </b>
      </span>

      {itemsSummary(order) && <span className="ord-name">{itemsSummary(order)}</span>}

      <span className="ord-sub">
        <span>{order.site_name ?? NO_SITE}</span>
        <span className="ord-id kz-num">{order.display_no}</span>
      </span>

      {!cancelled && (
        // ★`.obar` を当てない＝`ProgressBar variant="bar"` が5分割の flex を自前で持っており、
        //   外側にもう1枚 flex を被せるとバーが文字幅まで縮む（器はマージンだけ足す）。
        <div data-testid={`progress-${order.order_id}`} style={{ marginTop: "var(--kz-space-2)" }}>
          <ProgressBar steps={order.progress_steps} currentIndex={order.progress_index} variant="bar" />
        </div>
      )}
    </Link>
  );
}
